// Floating Cart Manager - Handles the floating cart UI independently
//
// On pages where rental-cart.js is loaded, RentalCart owns the badge count
// and visibility.  This manager only enforces drawer-hide in that case.
// On all other pages (home, collections, etc.) this manager reads the
// customer-specific localStorage key to show the correct count.

class FloatingCartManager {
  constructor() {
    this._isDrawerOpen = false;
    this.initialize();
  }

  initialize() {
    this.removeDuplicateCarts();
    this.createFloatingCart();
    this.setupEventListeners();
    this.setupDrawerDetection();
    // Check if we just came back from checkout before first update
    this._checkPostCheckout().then(() => this.update());
  }

  removeDuplicateCarts() {
    const carts = document.querySelectorAll('.floating-rental-cart');
    if (carts.length > 1) {
      for (let i = 1; i < carts.length; i++) {
        carts[i].parentNode.removeChild(carts[i]);
      }
    }
  }

  createFloatingCart() {
    if (!document.querySelector('.floating-rental-cart')) {
      const cartHTML = `
        <div class="floating-rental-cart" style="display: none;">
          <a href="/pages/rental-cart" class="floating-cart-btn" title="View Rental Cart">
            <span class="cart-icon">🛒</span>
            <span class="cart-count rental-cart-count">0</span>
          </a>
        </div>
      `;
      document.body.insertAdjacentHTML('beforeend', cartHTML);

      const style = document.createElement('style');
      style.textContent = `
        .floating-rental-cart {
          position: fixed !important;
          bottom: 30px !important;
          left: 30px !important;
          right: auto !important;
          z-index: 9999 !important;
        }
      `;
      document.head.appendChild(style);
    }
  }

  // ------------------------------------------------------------------
  // Data helpers
  // ------------------------------------------------------------------

  _getCustomerId() {
    // 1. In-memory RentalCart (most reliable)
    if (window.rentalCart && window.rentalCart.customerId) {
      return window.rentalCart.customerId;
    }
    // 2. DOM data attribute
    const container = document.querySelector('[data-customer-id]');
    if (container && container.dataset.customerId) return container.dataset.customerId;
    // 3. Meta tag
    const meta = document.querySelector('meta[name="customer-id"]');
    if (meta && meta.content) return meta.content;
    return null;
  }

  _getCartCount() {
    // Prefer the in-memory RentalCart instance (always up-to-date)
    if (window.rentalCart && window.rentalCart.isInitialized) {
      return window.rentalCart.cart.length;
    }

    // Fallback: read from the customer-specific localStorage key
    try {
      const customerId = this._getCustomerId();
      if (customerId) {
        const raw = localStorage.getItem(`rental_cart_${customerId}`);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) return parsed.length;
        }
      }
    } catch (e) { /* ignore */ }

    return 0;
  }

  // ------------------------------------------------------------------
  // Post-checkout auto-clear
  // ------------------------------------------------------------------

  async _checkPostCheckout() {
    // Skip if RentalCart is loaded — it handles this itself
    if (window.rentalCart && window.rentalCart.isInitialized) return;

    const pending = sessionStorage.getItem('pending_checkout_cart');
    if (!pending) return;

    const customerId = this._getCustomerId();
    if (!customerId) return;

    const cartKey = `rental_cart_${customerId}`;
    const raw = localStorage.getItem(cartKey);
    if (!raw) { sessionStorage.removeItem('pending_checkout_cart'); return; }

    let cartItems;
    try { cartItems = JSON.parse(raw); } catch { return; }
    if (!Array.isArray(cartItems) || cartItems.length === 0) {
      sessionStorage.removeItem('pending_checkout_cart');
      return;
    }

    // Fetch active rentals from the API
    try {
      const apiBase = window.__BOOKBUGS_CONFIG?.API_BASE_URL
        || 'https://order-return-testing-cjlu.onrender.com/api';
      const resp = await fetch(
        `${apiBase}/returns?customer_id=${customerId}`,
        { cache: 'no-store' }
      );
      if (!resp.ok) return;
      const data = await resp.json();
      const activeVariantIds = new Set(
        (data.active || []).map(r => String(r.variant_id))
      );

      const allInRentals = cartItems.every(item =>
        activeVariantIds.has(String(item.variantId))
      );

      if (allInRentals) {
        console.log('🎉 FloatingCartManager: checkout confirmed — clearing cart');
        localStorage.removeItem(cartKey);
        sessionStorage.removeItem('pending_checkout_cart');
      }
    } catch (e) {
      console.warn('FloatingCartManager: post-checkout check failed', e);
    }
  }

  // ------------------------------------------------------------------
  // Update cycle
  // ------------------------------------------------------------------

  update() {
    const floatingCart = document.querySelector('.floating-rental-cart');
    if (!floatingCart) return;

    // When RentalCart is initialized on this page it already maintains
    // the badge count and floating-cart visibility via updateCartCount().
    // We only need to enforce drawer-hide to avoid two systems fighting
    // over the same DOM elements.
    if (window.rentalCart && window.rentalCart.isInitialized) {
      if (this._isDrawerOpen) this._hide();
      return;
    }

    // RentalCart not (yet) available — we handle count + visibility
    const count = this._getCartCount();

    // Update badge
    floatingCart.querySelectorAll('.rental-cart-count').forEach(el => {
      el.textContent = count;
    });

    if (this._isDrawerOpen || count === 0) {
      floatingCart.style.display = 'none';
    } else {
      floatingCart.style.display = 'block';
      floatingCart.style.opacity = '1';
    }
  }

  _hide() {
    const el = document.querySelector('.floating-rental-cart');
    if (el) {
      el.style.opacity = '0';
      setTimeout(() => {
        if (el.style.opacity === '0') el.style.display = 'none';
      }, 300);
    }
  }

  setupEventListeners() {
    // Cart changed in this tab (fired by rental-cart.js / product-rental.js)
    document.addEventListener('rentalCartUpdate', () => this.update());

    // Cart changed in another tab
    window.addEventListener('storage', (e) => {
      if (e.key && e.key.startsWith('rental_cart_')) this.update();
    });
  }

  setupDrawerDetection() {
    // Robust drawer toggle detection:
    // - Listen to known toggle buttons and read `aria-expanded` when available
    // - Watch body class mutations as a fallback
    const toggleSelectors = '[data-toggle-drawer], .menu-drawer-toggle, .hamburger-menu, .header__menu-toggle, .site-header__menu-toggle, [aria-label*="menu" i], [aria-label*="navigation" i], .icon-hamburger';
    const toggles = document.querySelectorAll(toggleSelectors);

    toggles.forEach(btn => {
      btn.addEventListener('click', () => {
        // Let the theme update aria attributes/classes then check state
        setTimeout(() => {
          const expanded = btn.getAttribute && btn.getAttribute('aria-expanded');
          if (expanded !== null) {
            const isOpen = expanded === 'true' || btn.classList.contains('is-active') || btn.classList.contains('open');
            this._isDrawerOpen = !!isOpen;
            if (this._isDrawerOpen) this._hide(); else this.update();
            return;
          }
          // Fallback to DOM inspection
          this.checkDrawerState();
        }, 80);
      });
    });

    // MutationObserver fallback (watch body/class changes produced by theme)
    const observer = new MutationObserver(() => this.checkDrawerState());
    observer.observe(document.body, { attributes: true, attributeFilter: ['class', 'data-drawer-open'] });

    // Also catch explicit close buttons inside drawers (X buttons)
    document.addEventListener('click', (e) => {
      const closeBtn = e.target.closest('.icon-close, .drawer__close, .close-drawer, .drawer__toggle--close');
      if (closeBtn) setTimeout(() => this.checkDrawerState(), 60);
    });

    // ESC key as a close event
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') setTimeout(() => this.checkDrawerState(), 60);
    });
  }

  checkDrawerState() {
    // Check if drawer/modal is open by looking for common indicators
    const isDrawerOpen = 
      document.body.classList.contains('drawer-open') ||
      document.body.classList.contains('menu-open') ||
      document.body.classList.contains('sidebar-open') ||
      document.body.classList.contains('navigation-open') ||
      document.documentElement.getAttribute('data-drawer-open') === 'true' ||
      document.querySelector('[role="dialog"]:not([hidden])') !== null ||
      document.querySelector('.drawer:not([hidden])') !== null ||
      document.querySelector('.side-menu:not([hidden])') !== null ||
      document.querySelector('.mobile-sidebar:not([hidden])') !== null;

    if (isDrawerOpen !== this._isDrawerOpen) {
      this._isDrawerOpen = isDrawerOpen;
      if (this._isDrawerOpen) {
        this._hide();
      } else {
        this.update();
      }
    }
  }

}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.floatingCartManager = new FloatingCartManager();
  });
} else {
  window.floatingCartManager = new FloatingCartManager();
}
